import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  getAllPositionDetails,
  getPositions,
  getStockFundamentals,
  getStockNews,
  getStockPrice,
  getStockPriceHistory,
} from "../../services/portfolioService";
import { useCurrency } from "../Auth/AuthContext";

// ─── helpers ────────────────────────────────────────────────────────────────

function fmtLarge(n) {
  if (n == null) return "N/A";
  if (n >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  return Number(n).toLocaleString();
}

function fmtNum(val, decimals = 2, prefix = "", suffix = "") {
  if (val == null) return "N/A";
  return `${prefix}${parseFloat(val).toFixed(decimals)}${suffix}`;
}

const RANGES = ["1d", "1mo", "3mo", "6mo", "1y", "2y", "5y"];
const RANGE_LABELS = {
  "1d": "1D",
  "1mo": "1M",
  "3mo": "3M",
  "6mo": "6M",
  "1y": "1Y",
  "2y": "2Y",
  "5y": "5Y",
};

// ─── small stat cell ────────────────────────────────────────────────────────

const Stat = ({ label, value, color }) => (
  <Box sx={{ p: 1.5 }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", fontWeight: 500, mb: 0.25 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 700, color: color || "text.primary" }}
    >
      {value}
    </Typography>
  </Box>
);

// ─── main component ─────────────────────────────────────────────────────────

const StockDetailPage = () => {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const { convertAndFormat } = useCurrency();

  const tickerUpper = ticker?.toUpperCase();

  const [price, setPrice] = useState(null);
  const [fundamentals, setFundamentals] = useState(null);
  const [positions, setPositions] = useState([]);
  const [positionDetails, setPositionDetails] = useState({});
  const [news, setNews] = useState([]);
  const [history, setHistory] = useState([]);

  const [tab, setTab] = useState(0);
  const [range, setRange] = useState("1y");

  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [newsLoading, setNewsLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── initial load: price + fundamentals + positions ──────────────────────
  useEffect(() => {
    if (!tickerUpper) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [priceResult, fundamentalsResult, positionsResult, detailsResult] =
          await Promise.allSettled([
            getStockPrice(tickerUpper),
            getStockFundamentals(tickerUpper),
            getPositions(),
            getAllPositionDetails(),
          ]);

        if (cancelled) return;

        // Price is the only critical piece — show error only if it fails
        if (priceResult.status === "rejected") {
          setError(priceResult.reason?.message || "Failed to load stock data");
          return;
        }

        setPrice(priceResult.value);
        setFundamentals(fundamentalsResult.status === "fulfilled" ? fundamentalsResult.value : null);

        if (positionsResult.status === "fulfilled") {
          setPositions(positionsResult.value.filter((p) => p.ticker_symbol === tickerUpper));
        }

        if (detailsResult.status === "fulfilled") {
          const filtered = {};
          Object.entries(detailsResult.value).forEach(([key, val]) => {
            if (key.startsWith(`${tickerUpper}-`)) filtered[key] = val;
          });
          setPositionDetails(filtered);
        }
      } catch (err) {
        if (!cancelled)
          setError(err.message || "Failed to load stock data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [tickerUpper]);

  // ── history: re-fetch when range changes (skip 1D — uses intraday_data) ──
  useEffect(() => {
    if (!tickerUpper || range === "1d") return;
    let cancelled = false;

    const load = async () => {
      try {
        setHistoryLoading(true);
        const data = await getStockPriceHistory(tickerUpper, range);
        if (!cancelled) setHistory(data.history || []);
      } catch (err) {
        console.error("History fetch failed:", err);
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [tickerUpper, range]);

  // ── news: load lazily when News tab is opened ────────────────────────────
  useEffect(() => {
    if (tab !== 3 || news.length > 0 || !tickerUpper) return;
    let cancelled = false;

    const load = async () => {
      try {
        setNewsLoading(true);
        const data = await getStockNews(tickerUpper, 8);
        if (!cancelled) setNews(data);
      } catch (err) {
        console.error("News fetch failed:", err);
      } finally {
        if (!cancelled) setNewsLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [tab, tickerUpper, news.length]);

  // ── chart data ───────────────────────────────────────────────────────────
  const intradayRaw =
    price?.intraday_data || price?.intradayData || [];

  const chartData =
    range === "1d"
      ? intradayRaw
          .filter((d) => d.close != null)
          .map((d) => ({
            label: format(new Date(d.timestamp * 1000), "HH:mm"),
            price: parseFloat(d.close),
          }))
      : history
          .filter((d) => d.close != null)
          .map((d) => ({
            label: d.date,
            price: parseFloat(d.close),
          }));

  const formatXTick = (label) => {
    if (!label || range === "1d") return label;
    try {
      const d = parseISO(label);
      return range === "2y" || range === "5y"
        ? format(d, "MMM yy")
        : format(d, "MMM d");
    } catch {
      return label;
    }
  };

  const isUp =
    price?.price_change != null ? Number(price.price_change) >= 0 : null;
  const lineColor = isUp === false ? "#c62828" : "#2e7d32";

  const currency = price?.currency || "";

  // ── renders ──────────────────────────────────────────────────────────────

  const renderChart = () => (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Price chart
        </Typography>
        <ToggleButtonGroup
          value={range}
          exclusive
          onChange={(_, v) => { if (v) setRange(v); }}
          size="small"
        >
          {RANGES.map((r) => (
            <ToggleButton
              key={r}
              value={r}
              sx={{ px: 1.5, py: 0.25, fontSize: "0.7rem", minWidth: 32 }}
            >
              {RANGE_LABELS[r]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {historyLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress size={28} />
        </Box>
      ) : chartData.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography variant="body2" color="text.secondary">
            No chart data available
          </Typography>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart
            data={chartData}
            margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              tickFormatter={formatXTick}
              interval="preserveStartEnd"
              minTickGap={50}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              domain={["auto", "auto"]}
              tickFormatter={(v) => v.toFixed(2)}
              width={64}
            />
            <Tooltip
              formatter={(v) => [`${currency} ${v.toFixed(2)}`, "Price"]}
              labelFormatter={(l) =>
                range === "1d" ? l : formatXTick(l)
              }
            />
            <Line
              type="monotone"
              dataKey="price"
              stroke={lineColor}
              dot={false}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Box>
  );

  const renderStats = () => {
    const f = fundamentals;
    if (!f) return null;

    const ratingKey = (f.analyst_rating || "").toLowerCase();
    const hasRating = ratingKey && ratingKey !== "none";
    const analystColor =
      ratingKey === "buy" || ratingKey === "strong_buy"
        ? "success"
        : ratingKey === "sell" || ratingKey === "strong_sell"
        ? "error"
        : "default";

    const currentPrice = price?.current_price ? parseFloat(price.current_price) : null;
    const targetMean = f.target_price_mean ? parseFloat(f.target_price_mean) : null;
    const targetUpside =
      currentPrice && targetMean && currentPrice > 0
        ? ((targetMean - currentPrice) / currentPrice) * 100
        : null;

    return (
      <Box>
        {/* Analyst consensus row */}
        {(hasRating || targetMean) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
            {hasRating && (
              <Chip
                label={ratingKey.replace(/_/g, " ").toUpperCase()}
                color={analystColor}
                size="small"
              />
            )}
            {targetMean && (
              <Typography variant="body2" color="text.secondary">
                Target: {currency} {targetMean.toFixed(2)}
                {f.target_price_low && f.target_price_high && (
                  <> ({parseFloat(f.target_price_low).toFixed(0)}–
                  {parseFloat(f.target_price_high).toFixed(0)})</>
                )}
                {f.analyst_count && ` · ${f.analyst_count} analyst${f.analyst_count !== 1 ? "s" : ""}`}
                {targetUpside != null && (
                  <span style={{ color: targetUpside >= 0 ? "#2e7d32" : "#c62828", marginLeft: 4 }}>
                    ({targetUpside >= 0 ? "+" : ""}{targetUpside.toFixed(1)}% vs current)
                  </span>
                )}
              </Typography>
            )}
          </Box>
        )}

        {/* Stats grid */}
        <Grid container>
          {[
            ["Market Cap", fmtLarge(f.market_cap)],
            ["P/E (TTM)", fmtNum(f.pe_ratio)],
            ["Fwd P/E", fmtNum(f.forward_pe)],
            ["EPS (TTM)", fmtNum(f.eps, 2, `${currency} `)],
            ["Beta", fmtNum(f.beta)],
            [
              "Div Yield",
              fmtNum(
                f.dividend_yield != null ? f.dividend_yield * 100 : null,
                2,
                "",
                "%",
              ),
            ],
            ["Price/Book", fmtNum(f.price_to_book)],
            ["Fwd EPS", fmtNum(f.forward_eps, 2, `${currency} `)],
            [
              "52W High",
              fmtNum(price?.fifty_two_week_high, 2, `${currency} `),
            ],
            [
              "52W Low",
              fmtNum(price?.fifty_two_week_low, 2, `${currency} `),
            ],
            ["50D Avg", fmtNum(f.fifty_day_avg, 2, `${currency} `)],
            ["200D Avg", fmtNum(f.two_hundred_day_avg, 2, `${currency} `)],
          ].map(([label, value]) => (
            <Grid item xs={6} sm={3} key={label}>
              <Stat label={label} value={value} />
            </Grid>
          ))}
        </Grid>

        {/* Company description */}
        {f.description && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, textTransform: "uppercase", color: "text.secondary" }}
            >
              About
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, lineHeight: 1.65 }}
            >
              {f.description.length > 600
                ? f.description.slice(0, 600) + "…"
                : f.description}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.75, mt: 1.5, flexWrap: "wrap" }}>
              {f.sector && (
                <Chip label={f.sector} size="small" variant="outlined" />
              )}
              {f.industry && (
                <Chip label={f.industry} size="small" variant="outlined" />
              )}
              {f.country && (
                <Chip label={f.country} size="small" variant="outlined" />
              )}
              {f.employees && (
                <Chip
                  label={`${fmtLarge(f.employees)} employees`}
                  size="small"
                  variant="outlined"
                />
              )}
              {f.website && (
                <Chip
                  label="Website"
                  size="small"
                  variant="outlined"
                  icon={<OpenInNewIcon sx={{ fontSize: "12px !important" }} />}
                  component="a"
                  href={f.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                />
              )}
            </Box>
          </>
        )}
      </Box>
    );
  };

  const renderMyPosition = () => {
    if (positions.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography color="text.secondary">
            You don't hold any {tickerUpper} shares.
          </Typography>
        </Box>
      );
    }

    const allTx = Object.values(positionDetails)
      .flat()
      .sort((a, b) => new Date(b.purchase_date) - new Date(a.purchase_date));

    return (
      <Box>
        {positions.map((pos) => (
          <Paper
            key={pos.broker_id}
            variant="outlined"
            sx={{ mb: 2, p: 2 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              {pos.broker_name}
            </Typography>
            <Grid container>
              {[
                ["Shares", parseFloat(pos.net_quantity).toFixed(4)],
                ["Avg Cost", fmtNum(pos.avg_price, 2, `${pos.currency} `)],
                [
                  "Total Cost",
                  convertAndFormat(pos.total_cost, pos.currency),
                ],
                [
                  "Market Value",
                  convertAndFormat(pos.current_value, pos.currency),
                ],
                [
                  "Unrealized P/L",
                  convertAndFormat(pos.unrealized_pl, pos.currency),
                  pos.unrealized_pl >= 0 ? "success.main" : "error.main",
                ],
                [
                  "Return",
                  fmtNum(pos.unrealized_pl_percent, 2, "", "%"),
                  pos.unrealized_pl_percent >= 0
                    ? "success.main"
                    : "error.main",
                ],
              ].map(([label, value, color]) => (
                <Grid item xs={6} sm={4} key={label}>
                  <Stat label={label} value={value} color={color} />
                </Grid>
              ))}
            </Grid>
          </Paper>
        ))}

        {allTx.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Transaction History
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allTx.map((tx) => (
                  <TableRow key={tx.id} hover>
                    <TableCell>
                      {format(parseISO(tx.purchase_date), "dd MMM yyyy")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tx.transaction_type}
                        size="small"
                        color={
                          tx.transaction_type === "BUY"
                            ? "success"
                            : tx.transaction_type === "SELL"
                            ? "error"
                            : "default"
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {parseFloat(tx.quantity).toFixed(4)}
                    </TableCell>
                    <TableCell align="right">
                      {tx.purchase_price != null
                        ? fmtNum(tx.purchase_price)
                        : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {tx.total_cost
                        ? convertAndFormat(tx.total_cost, tx.currency)
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    );
  };

  const renderNews = () => {
    if (newsLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (news.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography color="text.secondary">No news found.</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {news.map((item) => (
          <Paper
            key={item.id}
            variant="outlined"
            component="a"
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              p: 2,
              display: "flex",
              gap: 2,
              textDecoration: "none",
              color: "inherit",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            {item.thumbnail && (
              <Box
                component="img"
                src={item.thumbnail}
                alt=""
                sx={{
                  width: 80,
                  height: 60,
                  objectFit: "cover",
                  borderRadius: 1,
                  flexShrink: 0,
                }}
              />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, lineHeight: 1.4, mb: 0.5 }}
              >
                {item.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.publisher}
                {item.publishedAt &&
                  ` · ${format(parseISO(item.publishedAt), "dd MMM yyyy")}`}
              </Typography>
            </Box>
            <OpenInNewIcon
              sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0, mt: 0.25 }}
            />
          </Paper>
        ))}
      </Box>
    );
  };

  // ── loading / error guards ───────────────────────────────────────────────

  if (loading) {
    return (
      <Container sx={{ display: "flex", justifyContent: "center", pt: 10 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ pt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // ── page ─────────────────────────────────────────────────────────────────

  const priceChange = price?.price_change;
  const priceChangePct = price?.price_change_percent;
  const currentPrice = price?.current_price ?? price?.currentPrice;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Back */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
        <IconButton size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body2" color="text.secondary">
          Back
        </Typography>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            {tickerUpper}
          </Typography>
          {fundamentals?.company_name && (
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
              {fundamentals.company_name}
            </Typography>
          )}
        </Box>

        {price && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1, flexWrap: "wrap" }}
          >
            <Typography variant="h3" sx={{ fontWeight: 700 }}>
              {currency} {currentPrice != null ? parseFloat(currentPrice).toFixed(2) : "—"}
            </Typography>
            {priceChange != null && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {isUp ? (
                  <TrendingUpIcon sx={{ color: "success.main" }} />
                ) : (
                  <TrendingDownIcon sx={{ color: "error.main" }} />
                )}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: isUp ? "success.main" : "error.main",
                  }}
                >
                  {isUp ? "+" : ""}
                  {parseFloat(priceChange).toFixed(2)}
                  {priceChangePct != null &&
                    ` (${isUp ? "+" : ""}${parseFloat(priceChangePct).toFixed(2)}%)`}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {price?.exchange && (
          <Typography variant="caption" color="text.secondary">
            {price.exchange} · {currency}
            {price.last_updated &&
              ` · Updated ${format(new Date(price.last_updated), "HH:mm")}`}
          </Typography>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Overview" />
          <Tab label="My Position" />
          <Tab label="Dividends" />
          <Tab label="News" />
        </Tabs>
      </Box>

      {/* Overview */}
      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>{renderChart()}</CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: "text.secondary",
                    mb: 1.5,
                  }}
                >
                  Key Statistics
                </Typography>
                {renderStats()}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* My Position */}
      {tab === 1 && renderMyPosition()}

      {/* Dividends — placeholder */}
      {tab === 2 && (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography color="text.secondary">
            Dividend history coming soon.
          </Typography>
        </Box>
      )}

      {/* News */}
      {tab === 3 && renderNews()}
    </Container>
  );
};

export default StockDetailPage;
